import { useEffect, useState } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { PaymentPeriod, PremiumConfig } from 'src/api/internal/schema';
import { filterNulls } from 'src/utils/array';
import { ExperienceType, useExperienceContext } from 'src/context/experience';
import { useUserContext } from 'src/context/user';
import { PAGE_TEMPLATES, PageTemplate } from '../pageTemplates';

export const getPageTemplateOverride = (
  premiumConfig: PremiumConfig,
  intl: IntlShape,
  experienceType: ExperienceType,
): PageTemplate | null => {
  const { prices, uiSettingsOverrides: overrides } = premiumConfig;

  if (Object.keys(overrides).length === 0) return null;

  const findPrice = (period: PaymentPeriod) => prices.find(p => p.paymentPeriod === period);
  const getOverride = (key: string | undefined) => overrides[key || ''] || null;

  const orderedOverrides = filterNulls([
    getOverride(findPrice('lifetime')?.discount?.key),
    getOverride(findPrice('year')?.discount?.key),
    getOverride(findPrice('month')?.discount?.key),
    getOverride(findPrice('lifetime')?.key),
    getOverride(findPrice('year')?.key),
    getOverride(findPrice('month')?.key),
  ]);
  const override = orderedOverrides[0];

  if (!override) return null;

  const baseTemplate =
    (override.templateId ? PAGE_TEMPLATES[override.templateId] : null) ||
    (experienceType === '2024' ? PAGE_TEMPLATES.default2024 : PAGE_TEMPLATES.default);

  const missingMessage = '__MISSING__';
  const isValidMessage = (messageId: string | undefined) => {
    if (!messageId) return false;

    const formattedMessage = intl.formatMessage({ id: messageId, defaultMessage: missingMessage });

    if (formattedMessage === missingMessage) return false;

    return !formattedMessage.includes('{');
  };

  const { headerImageUrl, titleMessageId, subtitleMessageId } = override;

  return {
    ...baseTemplate,
    ...(headerImageUrl ? { headerImageUrl } : {}),
    ...(isValidMessage(titleMessageId) ? { titleMessageId } : {}),
    ...(isValidMessage(subtitleMessageId) ? { subtitleMessageId } : {}),
  };
};

export const usePageTemplate = (premiumConfig: PremiumConfig | undefined): PageTemplate | null => {
  const intl = useIntl();
  const { experienceType } = useExperienceContext();
  const { user, loading: isLoadingUser } = useUserContext();
  const [pageTemplate, setPageTemplate] = useState<PageTemplate | null>(null);

  useEffect(() => {
    if (pageTemplate || !premiumConfig || isLoadingUser) {
      return;
    }

    const templateExperience = user ? experienceType : '2025';

    if (!templateExperience) return;

    setPageTemplate(
      getPageTemplateOverride(premiumConfig, intl, templateExperience) ||
        (templateExperience === '2024' ? PAGE_TEMPLATES.default2024 : PAGE_TEMPLATES.default),
    );
  }, [pageTemplate, premiumConfig, intl, user, isLoadingUser, experienceType]);

  return pageTemplate;
};
